/**
 * Shop owner history tab: appointments list, filters, sort, status actions, client profile modal.
 */
import React, { useState, useMemo, useRef, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import {
  FaUser,
  FaKey,
  FaCut,
  FaCalendarAlt,
  FaClock,
  FaDollarSign,
  FaEnvelope,
  FaPhone,
  FaChartBar,
  FaCheckCircle,
  FaTimesCircle,
  FaMoneyBillAlt,
  FaClipboardList,
  FaChevronDown,
} from 'react-icons/fa';
import { useI18n } from '../../contexts/I18n';
import { http } from '../../lib/http';
import DateIcon from '../DateIcon';
import { cleanText } from './utils';
import { sortShopOwnerAppointments } from '../../utils/appointmentSort';

const STATUSES = ['all', 'pending', 'accepted', 'declined', 'completed', 'cancelled'];

function formatStatus(status) {
  const s = String(status || 'pending');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export default function HistoryTab({ appts, updateStatus }) {
  const { t, lang } = useI18n();
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('');
  const dateInputRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [profileOpen, setProfileOpen] = useState(false);
  const [loadingProf, setLoadingProf] = useState(false);
  const [clientInfo, setClientInfo] = useState(null);
  const [selectedAppt, setSelectedAppt] = useState(null);
  const [statsOpen, setStatsOpen] = useState(false);
  const scrollPositionRef = useRef(0);
  const scrollContainerRef = useRef(null);
  const clickedElementPositionRef = useRef(null);
  const modalOverlayRef = useRef(null);

  useLayoutEffect(() => {
    if (profileOpen) {
      const container = scrollContainerRef.current;
      const savedScroll = scrollPositionRef.current;
      if (!container) {
        document.body.style.position = 'fixed';
        document.body.style.top = `-${savedScroll}px`;
        document.body.style.width = '100%';
      }
      document.body.style.overflow = 'hidden';
      if (modalOverlayRef.current) {
        requestAnimationFrame(() => {
          modalOverlayRef.current.scrollTop = 0;
        });
      }
    } else {
      const savedScroll = scrollPositionRef.current;
      const container = scrollContainerRef.current;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      requestAnimationFrame(() => {
        if (container && typeof container.scrollTo === 'function') {
          container.scrollTo(0, savedScroll);
        } else {
          window.scrollTo(0, savedScroll);
        }
        scrollPositionRef.current = 0;
        scrollContainerRef.current = null;
        clickedElementPositionRef.current = null;
      });
    }
  }, [profileOpen]);

  const filteredAndSortedAppts = useMemo(() => {
    let filtered = appts;
    if (statusFilter !== 'all') filtered = filtered.filter((a) => a.status === statusFilter);
    if (dateFilter) filtered = filtered.filter((a) => a.selectedDate >= dateFilter);
    if (searchQuery) {
      filtered = filtered.filter(
        (a) =>
          a.user?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          a.serviceName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          a.selectedDate?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    return sortShopOwnerAppointments(filtered, sortBy === 'name' ? 'name' : sortBy, sortOrder);
  }, [appts, statusFilter, dateFilter, searchQuery, sortBy, sortOrder]);

  const stats = useMemo(() => {
    const total = appts.length;
    const completed = appts.filter((a) => a.status === 'completed').length;
    const pending = appts.filter((a) => a.status === 'pending').length;
    const accepted = appts.filter((a) => a.status === 'accepted').length;
    const cancelled = appts.filter((a) => a.status === 'cancelled' || a.status === 'declined').length;
    const totalRevenue = appts
      .filter((a) => a.status === 'completed')
      .reduce((sum, a) => sum + (parseFloat(a.totalPrice) || 0), 0);
    return { total, completed, pending, accepted, cancelled, totalRevenue };
  }, [appts]);

  const closeProfileModal = () => {
    clickedElementPositionRef.current = null;
    setProfileOpen(false);
    setSelectedAppt(null);
    setClientInfo(null);
    setStatsOpen(false);
  };

  const viewProfile = async (booking, event) => {
    try {
      const scrollEl = event?.currentTarget?.closest?.('.content-area') || document.querySelector('.content-area');
      scrollContainerRef.current = scrollEl || null;
      scrollPositionRef.current = scrollEl ? scrollEl.scrollTop : (window.scrollY || document.documentElement.scrollTop || 0);

      const isLaptop = typeof window !== 'undefined' && window.innerWidth > 600;
      clickedElementPositionRef.current = null;
      if (isLaptop) {
        if (scrollEl && typeof scrollEl.scrollTo === 'function') scrollEl.scrollTo(0, 0);
        else window.scrollTo(0, 0);
      }

      setSelectedAppt(booking);
      setStatsOpen(false);
      setLoadingProf(true);
      setProfileOpen(true);
      setClientInfo(null);
      const clientId = booking.user?._id || booking.user;
      const data = await http.getJson(`/api/shopowner/client/${clientId}/profile`);
      setClientInfo(data);
    } catch (e) {
      setClientInfo({ error: t('ownerCouldNotLoadClient') || 'Could not load client profile.' });
    } finally {
      setLoadingProf(false);
    }
  };

  const formatHeroDate = (raw) => {
    if (!raw) return 'N/A';
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return String(raw);
    return d.toLocaleDateString(lang === 'ar' ? 'ar-EG' : 'en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatApptDate = (a) => {
    const raw = a.selectedDate ?? a.date ?? a.start ?? a.startTime ?? a.when ?? a.datetime ?? a.created ?? a.appointmentDate;
    const d = raw ? new Date(raw) : null;
    if (d && !Number.isNaN(d.getTime())) {
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      return `${day}-${month}-${year}`;
    }
    return raw ? String(raw) : 'N/A';
  };

  const openDatePicker = () => {
    const el = dateInputRef.current;
    if (!el) return;
    try {
      if (typeof el.showPicker === 'function') {
        el.showPicker();
      }
    } catch (_) {
      /* Native click on the overlay input still opens the calendar */
    }
  };

  const formatSelectedDate = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(`${iso}T12:00:00`);
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const yyyy = d.getFullYear();
      return `${dd}/${mm}/${yyyy}`;
    } catch {
      return iso;
    }
  };

  return (
    <section className="appointments-wrapper shopowner-history-card">
      <div className="shopowner-history-header">
        <div className="shopowner-history-header-content">
          <h2 className="shopowner-history-title-row">
            <span className="tab-title-icon-wrap" aria-hidden="true">
              <FaClipboardList className="shopowner-history-title-icon" size={18} />
            </span>
            {t('ownerMyAppointments')}
          </h2>
          <p className="shopowner-history-subtitle">{t('ownerMyAppointmentsSub')}</p>
        </div>
        <div className="shopowner-history-stats">
          <div className="stat-item">
            <span className="stat-icon" aria-hidden="true">
              <FaCalendarAlt />
            </span>
            <span className="stat-label">{t('dashStatTotal')}</span>
            <span className="stat-value">{cleanText(stats.total)}</span>
          </div>
          <div className="stat-item">
            <span className="stat-icon" aria-hidden="true">
              <FaCheckCircle />
            </span>
            <span className="stat-label">{t('dashStatCompleted')}</span>
            <span className="stat-value">{cleanText(stats.completed)}</span>
          </div>
          <div className="stat-item">
            <span className="stat-icon" aria-hidden="true">
              <FaClock />
            </span>
            <span className="stat-label">{t('dashStatPending')}</span>
            <span className="stat-value">{cleanText(stats.pending)}</span>
          </div>
          <div className="stat-item stat-item--revenue">
            <span className="stat-icon" aria-hidden="true">
              <FaDollarSign />
            </span>
            <span className="stat-label">{t('ownerRevenue')}</span>
            <span className="stat-value">{cleanText(`${stats.totalRevenue.toLocaleString('en-US')} EGP`)}</span>
          </div>
        </div>
      </div>

      <div className={`shopowner-history-filters ${lang === 'ar' ? 'rtl' : ''}`}>
        <div className="search-box">
          <input
            type="text"
            placeholder={t('dashSearchAppointments')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
          <span className="search-icon">🔍</span>
        </div>

        {/* Sibling of search so mobile can place date beside search */}
        <div
          className="date-input-container"
          onClick={openDatePicker}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              openDatePicker();
            }
          }}
          role="button"
          tabIndex={0}
          aria-label={t('ownerSelectDate') || 'Select Date'}
        >
          <div className={`date-display ${dateFilter ? 'filled' : ''}`} aria-hidden="true">
            {dateFilter ? (
              <span>{formatSelectedDate(dateFilter)}</span>
            ) : (
              <>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2" />
                  <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2" />
                  <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2" />
                  <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2" />
                </svg>
                <span>{t('ownerSelectDate')}</span>
              </>
            )}
          </div>
          <input
            ref={dateInputRef}
            type="date"
            className={`filter-date ${dateFilter ? 'filled' : 'empty'}`}
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            onClick={(e) => {
              e.stopPropagation();
              openDatePicker();
            }}
            tabIndex={-1}
            aria-hidden="true"
          />
        </div>

        <div className="filter-group">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="filter-select">
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s === 'all' ? t('ownerStatusAll') : s.replace(/^\w/, (c) => c.toUpperCase())}
              </option>
            ))}
          </select>

          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="filter-select">
            <option value="date">{t('ownerSortByDate')}</option>
            <option value="price">{t('ownerSortByPrice')}</option>
            <option value="name">{t('ownerSortByClient')}</option>
          </select>

          <button className="sort-toggle" onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}>
            {sortOrder === 'asc' ? '↑' : '↓'}
          </button>
        </div>
      </div>

      {filteredAndSortedAppts.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">
            <DateIcon />
          </div>
          <h3>{t('ownerEmptyAppts')}</h3>
          <p>{searchQuery || dateFilter ? t('dashTryAdjust') : t('ownerEmptyApptsHelp')}</p>
        </div>
      ) : (
        <div className={`shopowner-appointments-grid${lang === 'ar' ? ' rtl' : ''}`} dir={lang === 'ar' ? 'rtl' : 'ltr'}>
          {filteredAndSortedAppts.map((a, index) => (
            <article key={a._id} className="shopowner-appointment-card modern-appointment-card">
              <div className="modern-appointment-header">
                <div className="modern-appointment-client">
                  <div className="modern-appointment-client-info">
                    <div className="appointment-number-badge">
                      <span className="appointment-number-text">#{index + 1}</span>
                    </div>
                    <span className="booking-request-text">{t('ownerBookingRequest')}</span>
                  </div>
                  <div className={`modern-badge status-${a.status}`}>
                    <span className="modern-badge-text">{a.status || 'pending'}</span>
                  </div>
                </div>
              </div>

              <div className="modern-appointment-details">
                <div className="details-row details-row-first">
                  <div className="modern-detail-item detail-item-half">
                    <div className="modern-detail-icon-wrapper icon-appointment-id">
                      <FaKey className="detail-icon" />
                    </div>
                    <div className="detail-content">
                      <span className="modern-detail-label">{t('ownerAppointmentId')}</span>
                      <span className="modern-detail-value">{a._id || 'N/A'}</span>
                    </div>
                  </div>
                  <div className="modern-detail-item detail-item-half">
                    <div className="modern-detail-icon-wrapper icon-service">
                      <FaCut className="detail-icon" />
                    </div>
                    <div className="detail-content">
                      <span className="modern-detail-label">{t('ownerServiceNameLabel')}</span>
                      <span className="modern-detail-value">{a.serviceName || 'N/A'}</span>
                    </div>
                  </div>
                </div>

                <div className="details-row">
                  <div className="modern-detail-item detail-item-half">
                    <div className="modern-detail-icon-wrapper icon-date">
                      <FaCalendarAlt className="detail-icon" />
                    </div>
                    <div className="detail-content">
                      <span className="modern-detail-label">{t('ownerDateLabel')}</span>
                      <span className="modern-detail-value">{formatApptDate(a)}</span>
                    </div>
                  </div>
                  <div className="modern-detail-item detail-item-half">
                    <div className="modern-detail-icon-wrapper icon-time">
                      <FaClock className="detail-icon" />
                    </div>
                    <div className="detail-content">
                      <span className="modern-detail-label">{t('ownerTimeLabel')}</span>
                      <span className="modern-detail-value">{cleanText(a.selectedHour || 'N/A')}</span>
                    </div>
                  </div>
                </div>

                <div className="details-row">
                  {a.totalPrice != null && (
                    <div className="modern-detail-item detail-item-half">
                      <div className="modern-detail-icon-wrapper icon-price">
                        <FaDollarSign className="detail-icon" />
                      </div>
                      <div className="detail-content">
                        <span className="modern-detail-label">{t('labelPrice')}</span>
                        <span className="modern-detail-value price-value">
                          {cleanText(`${Number(a.totalPrice).toLocaleString('en-US')} EGP`)}
                        </span>
                      </div>
                    </div>
                  )}
                  <div className="modern-detail-item detail-item-half">
                    <div className="modern-detail-icon-wrapper icon-staff">
                      <FaUser className="detail-icon" />
                    </div>
                    <div className="detail-content">
                      <span className="modern-detail-label">{t('labelStaff')}</span>
                      <span className="modern-detail-value">{a.staffName || a.staff?.name || a.staff || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="appointment-actions modern-appointment-actions">
                {(a.status === 'pending' || a.status === 'accepted') && (
                  <div className="status-actions button-row">
                    {a.status === 'pending' && (
                      <>
                        <button className="btn accept action-button" onClick={() => updateStatus(a._id, 'accepted')}>
                          {t('ownerAccept')}
                        </button>
                        <button className="btn decline action-button" onClick={() => updateStatus(a._id, 'declined')}>
                          {t('ownerDecline')}
                        </button>
                      </>
                    )}
                    {a.status === 'accepted' && (
                      <>
                        <button className="btn done action-button" onClick={() => updateStatus(a._id, 'completed')}>
                          {t('ownerMarkComplete')}
                        </button>
                        <button className="btn cancel action-button" onClick={() => updateStatus(a._id, 'cancelled')}>
                          {t('ownerCancelBtn')}
                        </button>
                      </>
                    )}
                  </div>
                )}

                <button type="button" className="btn outline modern-profile-button" onClick={(e) => viewProfile(a, e)}>
                  {t('ownerViewClientProfile')}
                </button>
              </div>
            </article>
          ))}
        </div>
      )}

      {profileOpen &&
        ReactDOM.createPortal(
          <div
            ref={modalOverlayRef}
            className="client-modal-overlay"
            onClick={closeProfileModal}
            style={{
              display: clickedElementPositionRef.current ? 'block' : 'flex',
              alignItems: clickedElementPositionRef.current ? 'center' : 'flex-start',
              justifyContent: 'center',
              overflowY: 'auto',
              padding: clickedElementPositionRef.current ? '0' : '24px 0 0',
              backdropFilter: 'none',
              WebkitBackdropFilter: 'none',
              boxShadow: 'none',
              filter: 'none',
              WebkitFilter: 'none',
            }}
          >
            <div
              className="client-modal modern-profile-modal cal-booking-modal"
              dir={lang === 'ar' ? 'rtl' : 'ltr'}
              role="dialog"
              aria-label={t('dashBookingDetails') || 'Appointment Details'}
              onClick={(e) => e.stopPropagation()}
              style={
                clickedElementPositionRef.current
                  ? {
                      position: 'fixed',
                      top: `${clickedElementPositionRef.current.top}px`,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      maxHeight: 'calc(100vh - 40px)',
                      width: 'min(520px, 94vw)',
                      margin: '0',
                      zIndex: 1001,
                      boxShadow: 'none',
                      filter: 'none',
                      WebkitFilter: 'none',
                      textShadow: 'none',
                    }
                  : {
                      margin: 'auto',
                      maxHeight: 'calc(100vh - 40px)',
                      position: 'relative',
                      boxShadow: 'none',
                      filter: 'none',
                      WebkitFilter: 'none',
                      textShadow: 'none',
                    }
              }
            >
              <div className="modern-profile-modal-header cal-booking-modal-header">
                <div className="modern-profile-modal-header-content">
                  <div className="modern-profile-modal-title-container">
                    <span className="cal-booking-modal-icon-wrap">
                      <FaCalendarAlt className="modern-profile-modal-icon" />
                    </span>
                    <h3 className="modern-profile-modal-title">
                      {t('dashBookingDetails') || 'Appointment Details'}
                    </h3>
                  </div>
                </div>
                <button className="modern-profile-modal-close" onClick={closeProfileModal} aria-label="Close">
                  ×
                </button>
              </div>

              <div className="modern-profile-modal-body cal-booking-modal-body">
                {selectedAppt && (
                  <div className="modern-profile-section cal-booking-section">
                    <div className="cal-booking-hero">
                      <div className="cal-booking-hero-main">
                        <span className="cal-booking-hero-label">{t('ownerServiceNameLabel')}</span>
                        <h4 className="cal-booking-hero-service">{selectedAppt.serviceName || 'N/A'}</h4>
                        <div className="cal-booking-hero-id">
                          <span className="cal-booking-hero-id-label">{t('ownerAppointmentId')}</span>
                          <span className="cal-booking-hero-id-value">{selectedAppt._id || 'N/A'}</span>
                        </div>
                      </div>
                      <span
                        className={`cal-booking-status-pill status-${String(selectedAppt.status || 'pending').toLowerCase()}`}
                      >
                        {formatStatus(selectedAppt.status)}
                      </span>
                    </div>

                    <div className="cal-booking-grid">
                      <div className="cal-booking-tile">
                        <span className="cal-booking-tile-icon cal-booking-tile-icon--amber">
                          <FaCalendarAlt />
                        </span>
                        <div className="cal-booking-tile-text">
                          <span className="cal-booking-tile-label">{t('ownerDateLabel')}</span>
                          <span className="cal-booking-tile-value">
                            {cleanText(formatHeroDate(selectedAppt.selectedDate))}
                          </span>
                        </div>
                      </div>
                      <div className="cal-booking-tile">
                        <span className="cal-booking-tile-icon cal-booking-tile-icon--amber">
                          <FaClock />
                        </span>
                        <div className="cal-booking-tile-text">
                          <span className="cal-booking-tile-label">{t('ownerTimeLabel')}</span>
                          <span className="cal-booking-tile-value">
                            {cleanText(selectedAppt.selectedHour || 'N/A')}
                          </span>
                        </div>
                      </div>
                      <div className="cal-booking-tile">
                        <span className="cal-booking-tile-icon">
                          <FaUser />
                        </span>
                        <div className="cal-booking-tile-text">
                          <span className="cal-booking-tile-label">{t('labelStaff')}</span>
                          <span className="cal-booking-tile-value">
                            {selectedAppt.staffName || selectedAppt.staff?.name || selectedAppt.staff || 'N/A'}
                          </span>
                        </div>
                      </div>
                      {selectedAppt.totalPrice != null && (
                        <div className="cal-booking-tile cal-booking-tile--price">
                          <span className="cal-booking-tile-icon cal-booking-tile-icon--amber">
                            <FaDollarSign />
                          </span>
                          <div className="cal-booking-tile-text">
                            <span className="cal-booking-tile-label">{t('labelPrice')}</span>
                            <span className="cal-booking-tile-value">
                              {cleanText(`${Number(selectedAppt.totalPrice).toLocaleString('en-US')} EGP`)}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {loadingProf ? (
                  <div className="client-modal-loading">
                    <div>{t('ownerLoadingClientProfile')}</div>
                  </div>
                ) : clientInfo?.error ? (
                  <div className="client-modal-error">
                    <div>{clientInfo.error}</div>
                  </div>
                ) : clientInfo?.profile ? (
                  <div className="modern-profile-section cal-booking-client-section">
                    <div className="modern-profile-section-header">
                      <div className="modern-profile-section-header-left">
                        <div className="modern-profile-section-icon cal-booking-section-icon">
                          <FaUser />
                        </div>
                        <h4 className="modern-profile-section-title">{t('personalInformation')}</h4>
                      </div>
                    </div>

                    <div className="cal-booking-person-row">
                      <div className="cal-booking-person-name">
                        <span className="cal-booking-person-name-label">{t('name')}</span>
                        <span className="cal-booking-person-name-value">
                          {clientInfo.profile.name || 'N/A'}
                        </span>
                      </div>
                      <div className="cal-booking-person-actions">
                        {clientInfo.profile.phone && (
                          <a
                            href={`tel:${clientInfo.profile.phone}`}
                            className="cal-booking-icon-btn"
                            aria-label={t('callNow')}
                            title={clientInfo.profile.phone}
                          >
                            <FaPhone />
                          </a>
                        )}
                        {clientInfo.profile.email && (
                          <a
                            href={`mailto:${clientInfo.profile.email}`}
                            className="cal-booking-icon-btn"
                            aria-label={t('emailNow')}
                            title={clientInfo.profile.email}
                          >
                            <FaEnvelope />
                          </a>
                        )}
                      </div>
                    </div>

                    {clientInfo.stats && (
                      <div className="cal-booking-stats-dropdown">
                        <button
                          type="button"
                          className={`cal-booking-stats-toggle ${statsOpen ? 'is-open' : ''}`}
                          onClick={() => setStatsOpen((v) => !v)}
                          aria-expanded={statsOpen}
                        >
                          <span className="cal-booking-stats-toggle-left">
                            <FaChartBar className="cal-booking-stats-toggle-icon" aria-hidden />
                            {t('ownerBookingStats')}
                          </span>
                          <FaChevronDown className="cal-booking-stats-chevron" aria-hidden />
                        </button>

                        {statsOpen && (
                          <div className="cal-booking-stats-panel">
                            <div className="modern-stats-grid">
                              <div className="modern-stat-card">
                                <div className="modern-stat-icon">
                                  <FaCalendarAlt />
                                </div>
                                <div className="modern-stat-value">
                                  {cleanText(clientInfo.stats?.totalBookings || clientInfo.stats?.total || 0)}
                                </div>
                                <div className="modern-stat-label">{t('ownerTotalBookings')}</div>
                              </div>
                              <div className="modern-stat-card">
                                <div className="modern-stat-icon">
                                  <FaCheckCircle />
                                </div>
                                <div className="modern-stat-value">
                                  {cleanText(clientInfo.stats.completed || 0)}
                                </div>
                                <div className="modern-stat-label">{t('dashStatCompleted')}</div>
                              </div>
                              <div className="modern-stat-card">
                                <div className="modern-stat-icon">
                                  <FaTimesCircle />
                                </div>
                                <div className="modern-stat-value">
                                  {cleanText(clientInfo.stats.cancelled || 0)}
                                </div>
                                <div className="modern-stat-label">{t('ownerCancelled')}</div>
                              </div>
                              {clientInfo.stats?.spendEGP && (
                                <div className="modern-stat-card">
                                  <div className="modern-stat-icon">
                                    <FaMoneyBillAlt />
                                  </div>
                                  <div className="modern-stat-value">
                                    {cleanText(`${clientInfo.stats.spendEGP} EGP`)}
                                  </div>
                                  <div className="modern-stat-label">{t('ownerTotalSpentEGP')}</div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          </div>,
          document.body
        )}
    </section>
  );
}
